import {
  FilterDocument,
  FilterDocumentDurationFilter,
  FilterDocumentDurationTypes,
  FilterOptionKeys,
  GoalFilterDocument,
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
  ID,
  SortingMode,
  TaskGroupBy,
} from '@griply/types';

import { stripUndefined } from '../utils/strip-undefined';

const TASK_GROUP_BY_VALUES = Object.values(GroupByMode).filter(
  (value) => value !== GroupByMode.StartDate,
) as TaskGroupBy[];

const GOAL_GROUP_BY_VALUES: GoalGroupBy[] = [
  GroupByMode.None,
  GroupByMode.LifeArea,
  GroupByMode.StartDate,
  GroupByMode.Deadline,
];

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  return true;
};

export const coerceSortingMode = (value: unknown): SortingMode | undefined => {
  if (!Object.values(SortingMode).includes(value as SortingMode)) {
    return undefined;
  }
  return value as SortingMode;
};

export const coerceGoalSortingMode = (
  value: unknown,
): GoalSortingMode | undefined => {
  if (!Object.values(GoalSortingMode).includes(value as GoalSortingMode)) {
    return undefined;
  }
  return value as GoalSortingMode;
};

const coerceTaskGroupByMode = (value: unknown): TaskGroupBy | undefined => {
  if (!TASK_GROUP_BY_VALUES.includes(value as TaskGroupBy)) {
    return undefined;
  }
  return value as TaskGroupBy;
};

const coerceGoalGroupByMode = (value: unknown): GoalGroupBy | undefined => {
  if (!GOAL_GROUP_BY_VALUES.includes(value as GoalGroupBy)) {
    return undefined;
  }
  return value as GoalGroupBy;
};

const coerceCollapsedSections = (value: unknown): ID[] | null => {
  if (value === null) {
    return null;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter((entry): entry is ID => typeof entry === 'string');
};

const coerceShowCompleted = (value: unknown): boolean | null => {
  if (value === null) {
    return null;
  }
  if (typeof value !== 'boolean') {
    return null;
  }
  return value;
};

const coerceBooleanFlag = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const coerceDurationBound = (
  value: unknown,
): FilterDocumentDurationFilter['more'] => {
  if (!isPlainRecord(value)) {
    return null;
  }
  if (typeof value.value !== 'number') {
    return null;
  }
  if (
    !Object.values(FilterDocumentDurationTypes).includes(
      value.type as FilterDocumentDurationTypes,
    )
  ) {
    return null;
  }
  return {
    type: value.type as FilterDocumentDurationTypes,
    value: value.value,
  };
};

// Stripped: consumers spread the coerced document back into Firestore writes.
const coerceDurationFilter = (
  value: unknown,
): FilterDocumentDurationFilter | null => {
  if (!isPlainRecord(value)) {
    return null;
  }
  return stripUndefined({
    more: coerceDurationBound(value.more),
    less: coerceDurationBound(value.less),
    noDuration: coerceBooleanFlag(value.noDuration),
    hasDuration: coerceBooleanFlag(value.hasDuration),
  });
};

const coerceFilters = (
  value: unknown,
): Record<string, unknown> | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (!isPlainRecord(value)) {
    return undefined;
  }
  if (value[FilterOptionKeys.Duration] === undefined) {
    return { ...value };
  }
  return {
    ...value,
    [FilterOptionKeys.Duration]: coerceDurationFilter(
      value[FilterOptionKeys.Duration],
    ),
  };
};

const coerceOr = (
  value: unknown,
): Record<string, unknown>[] | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter(isPlainRecord).map((entry) => ({ ...entry }));
};

type CoercedBase = {
  collapsedSections?: ID[] | null;
  showCompleted?: boolean | null;
  filters?: Record<string, unknown> | null;
  or?: Record<string, unknown>[] | null;
};

const buildBaseDocument = (raw: Record<string, unknown>): CoercedBase => {
  const result: CoercedBase = {};

  if ('collapsedSections' in raw) {
    result.collapsedSections = coerceCollapsedSections(raw.collapsedSections);
  }

  if ('showCompleted' in raw) {
    result.showCompleted = coerceShowCompleted(raw.showCompleted);
  }

  if ('filters' in raw) {
    const filters = coerceFilters(raw.filters);
    if (filters !== undefined) {
      result.filters = filters;
    }
  }

  if ('or' in raw) {
    const or = coerceOr(raw.or);
    if (or !== undefined) {
      result.or = or;
    }
  }

  return result;
};

export const coerceFilterDocument = (
  raw: unknown,
): FilterDocument | undefined => {
  if (!isPlainRecord(raw)) {
    return undefined;
  }

  const result: FilterDocument = {
    ...buildBaseDocument(raw),
  } as FilterDocument;

  if ('sortingMode' in raw) {
    const sortingMode = coerceSortingMode(raw.sortingMode);
    if (sortingMode !== undefined) {
      result.sortingMode = sortingMode;
    }
  }

  if ('groupByMode' in raw) {
    const groupByMode = coerceTaskGroupByMode(raw.groupByMode);
    if (groupByMode !== undefined) {
      result.groupByMode = groupByMode;
    }
  }

  return result;
};

export const coerceGoalFilterDocument = (
  raw: unknown,
): GoalFilterDocument | undefined => {
  if (!isPlainRecord(raw)) {
    return undefined;
  }

  const result: GoalFilterDocument = {
    ...buildBaseDocument(raw),
  } as GoalFilterDocument;

  if ('sortingMode' in raw) {
    const sortingMode = coerceGoalSortingMode(raw.sortingMode);
    if (sortingMode !== undefined) {
      result.sortingMode = sortingMode;
    }
  }

  if ('groupByMode' in raw) {
    const groupByMode = coerceGoalGroupByMode(raw.groupByMode);
    if (groupByMode !== undefined) {
      result.groupByMode = groupByMode;
    }
  }

  return result;
};
